/**
 * Gloogum money core.
 * RULE: an amount is meaningless without its currency. Always pass both.
 * Prices are stored in the currency they were created in — never converted on write.
 */

export interface CurrencyDef {
  code: string;
  name: string;
  symbol: string;
  decimals: number;
}

export interface CountryDef {
  country_code: string;
  country_name: string;
  default_currency: string;
  locale: string;
  flag_emoji?: string;
}

/** Static fallback used before/if the DB config loads. Mirrors platform_currencies. */
export const FALLBACK_CURRENCIES: CurrencyDef[] = [
  { code: "NGN", name: "Nigerian Naira", symbol: "₦", decimals: 2 },
  { code: "GHS", name: "Ghanaian Cedi", symbol: "GH₵", decimals: 2 },
  { code: "KES", name: "Kenyan Shilling", symbol: "KSh", decimals: 2 },
  { code: "ZAR", name: "South African Rand", symbol: "R", decimals: 2 },
  { code: "XOF", name: "West African CFA Franc", symbol: "CFA", decimals: 0 },
  { code: "XAF", name: "Central African CFA Franc", symbol: "FCFA", decimals: 0 },
  { code: "RWF", name: "Rwandan Franc", symbol: "RF", decimals: 0 },
  { code: "UGX", name: "Ugandan Shilling", symbol: "USh", decimals: 0 },
  { code: "TZS", name: "Tanzanian Shilling", symbol: "TSh", decimals: 0 },
  { code: "ZMW", name: "Zambian Kwacha", symbol: "ZK", decimals: 2 },
  { code: "USD", name: "US Dollar", symbol: "$", decimals: 2 },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$", decimals: 2 },
  { code: "GBP", name: "British Pound", symbol: "£", decimals: 2 },
  { code: "EUR", name: "Euro", symbol: "€", decimals: 2 },
];

/** Static fallback mirror of platform_countries. */
export const FALLBACK_COUNTRIES: CountryDef[] = [
  { country_code: "NG", country_name: "Nigeria", default_currency: "NGN", locale: "en-NG", flag_emoji: "🇳🇬" },
  { country_code: "GH", country_name: "Ghana", default_currency: "GHS", locale: "en-GH", flag_emoji: "🇬🇭" },
  { country_code: "KE", country_name: "Kenya", default_currency: "KES", locale: "en-KE", flag_emoji: "🇰🇪" },
  { country_code: "ZA", country_name: "South Africa", default_currency: "ZAR", locale: "en-ZA", flag_emoji: "🇿🇦" },
  { country_code: "CI", country_name: "Côte d'Ivoire", default_currency: "XOF", locale: "fr-CI", flag_emoji: "🇨🇮" },
  { country_code: "CM", country_name: "Cameroon", default_currency: "XAF", locale: "fr-CM", flag_emoji: "🇨🇲" },
  { country_code: "SN", country_name: "Senegal", default_currency: "XOF", locale: "fr-SN", flag_emoji: "🇸🇳" },
  { country_code: "RW", country_name: "Rwanda", default_currency: "RWF", locale: "en-RW", flag_emoji: "🇷🇼" },
  { country_code: "UG", country_name: "Uganda", default_currency: "UGX", locale: "en-UG", flag_emoji: "🇺🇬" },
  { country_code: "TZ", country_name: "Tanzania", default_currency: "TZS", locale: "en-TZ", flag_emoji: "🇹🇿" },
  { country_code: "ZM", country_name: "Zambia", default_currency: "ZMW", locale: "en-ZM", flag_emoji: "🇿🇲" },
  { country_code: "CA", country_name: "Canada", default_currency: "CAD", locale: "en-CA", flag_emoji: "🇨🇦" },
  { country_code: "US", country_name: "United States", default_currency: "USD", locale: "en-US", flag_emoji: "🇺🇸" },
  { country_code: "GB", country_name: "United Kingdom", default_currency: "GBP", locale: "en-GB", flag_emoji: "🇬🇧" },
  { country_code: "EU", country_name: "Eurozone", default_currency: "EUR", locale: "en-IE", flag_emoji: "🇪🇺" },
];

let CURRENCY_INDEX: Record<string, CurrencyDef> = Object.fromEntries(
  FALLBACK_CURRENCIES.map((c) => [c.code, c]),
);

/** Called once the DB config is loaded so formatMoney() stays in sync everywhere. */
export function registerCurrencies(list: CurrencyDef[]) {
  if (!list?.length) return;
  CURRENCY_INDEX = { ...CURRENCY_INDEX, ...Object.fromEntries(list.map((c) => [c.code, c])) };
}

export function getCurrency(code?: string | null): CurrencyDef {
  const key = (code || "USD").toUpperCase();
  return CURRENCY_INDEX[key] ?? { code: key, name: key, symbol: `${key} `, decimals: 2 };
}

export function currencySymbol(code?: string | null): string {
  return getCurrency(code).symbol;
}

export function currencyDecimals(code?: string | null): number {
  return getCurrency(code).decimals;
}

export interface FormatMoneyOptions {
  /** Append the ISO code, e.g. "₦25,000 NGN". */
  showCode?: boolean;
  /** Force decimal places (defaults to the currency's own rule). */
  decimals?: number;
  /** Locale for grouping/separators. */
  locale?: string;
  /** Compact form, e.g. ₦1.2M. */
  compact?: boolean;
}

/**
 * formatMoney(25000, "NGN") -> "₦25,000"
 * formatMoney(500, "GHS")   -> "GH₵500.00"
 * formatMoney(100, "USD")   -> "$100.00"
 */
export function formatMoney(
  amount: number | string | null | undefined,
  currencyCode?: string | null,
  opts: FormatMoneyOptions = {},
): string {
  const def = getCurrency(currencyCode);
  const value = typeof amount === "string" ? Number(amount) : amount ?? 0;
  const safe = Number.isFinite(value) ? (value as number) : 0;
  const decimals = opts.decimals ?? def.decimals;
  let body: string;
  try {
    body = new Intl.NumberFormat(opts.locale || undefined, {
      minimumFractionDigits: opts.compact ? 0 : decimals,
      maximumFractionDigits: opts.compact ? 1 : decimals,
      notation: opts.compact ? "compact" : "standard",
    }).format(safe);
  } catch {
    body = safe.toFixed(decimals);
  }
  const sign = safe < 0 ? "-" : "";
  const abs = body.replace("-", "");
  const out = `${sign}${def.symbol}${abs}`;
  return opts.showCode ? `${out} ${def.code}` : out;
}

/** Estimated/secondary display, e.g. "≈ C$18.40". Never authoritative. */
export function formatApprox(
  amount: number | null | undefined,
  currencyCode?: string | null,
  opts: FormatMoneyOptions = {},
): string {
  if (amount == null || !Number.isFinite(amount)) return "";
  return `≈ ${formatMoney(amount, currencyCode, opts)}`;
}

/** Fixed-precision helpers — never accumulate float error on money. */
export function roundMoney(amount: number, currencyCode?: string | null): number {
  const d = currencyDecimals(currencyCode);
  const f = Math.pow(10, d);
  return Math.round((amount + Number.EPSILON) * f) / f;
}

export function toMinorUnits(amount: number, currencyCode?: string | null): number {
  const f = Math.pow(10, currencyDecimals(currencyCode));
  return Math.round(amount * f);
}

export function fromMinorUnits(minor: number, currencyCode?: string | null): number {
  const f = Math.pow(10, currencyDecimals(currencyCode));
  return minor / f;
}

/**
 * Convert using a USD-based rate map (1 USD = rates[X] X).
 * Returns null when a rate is missing — callers must then hide the estimate
 * rather than invent one.
 */
export function convertWithRates(
  amount: number,
  from: string,
  to: string,
  rates: Record<string, number>,
): number | null {
  const f = from?.toUpperCase();
  const t = to?.toUpperCase();
  if (!f || !t) return null;
  if (f === t) return roundMoney(amount, t);
  const rf = f === "USD" ? 1 : rates[f];
  const rt = t === "USD" ? 1 : rates[t];
  if (!rf || !rt) return null;
  return roundMoney((amount / rf) * rt, t);
}

export function countryToCurrency(countryCode?: string | null): string | null {
  if (!countryCode) return null;
  const c = FALLBACK_COUNTRIES.find(
    (x) => x.country_code.toLowerCase() === countryCode.toLowerCase(),
  );
  return c?.default_currency ?? null;
}

/** Browser locale -> currency suggestion (a suggestion only, never a hard override). */
export function currencyFromBrowserLocale(): string | null {
  try {
    const loc = navigator.language || "";
    const region = loc.split("-")[1];
    return countryToCurrency(region);
  } catch {
    return null;
  }
}
